import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Locate, Search, Mic, Star, ChevronDown, Megaphone, BadgeCheck, Heart } from "lucide-react";
import api, { API_CONFIGURED } from "@/lib/api";
import { normalizeShop, normalizeCategories, iconForCategory, fetchShopsNearby } from "@/lib/adapt";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";
import { useAuth } from "@/context/AuthContext";
import { isFavorite, toggleFavorite } from "@/lib/favorites";

const DEFAULT_CENTER = { lat: 10.776, lng: 76.654, name: "Palakkad" };
const RADII = [10, 20, 30, 50];

export default function MobileHome() {
  const { t } = useT();
  const nav = useNavigate();
  const [center, setCenter] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("nd_center") || "null");
      return s?.lat ? s : DEFAULT_CENTER;
    } catch (_) {
      return DEFAULT_CENTER;
    }
  });
  const [radius, setRadius] = useState(10);
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [catOpen, setCatOpen] = useState(false);
  const [geoResults, setGeoResults] = useState([]);
  const [geoQuery, setGeoQuery] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/categories")
      .then((r) => setCategories(normalizeCategories(r.data?.categories)))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    if (!API_CONFIGURED) {
      setLoading(false);
      setError({
        title: "App not configured",
        detail: "REACT_APP_NEARBYDEALS_API is missing in this build.",
      });
      return;
    }
    fetchShopsNearby(api, {
      lat: center.lat,
      lng: center.lng,
      radius,
      category: category === "all" ? undefined : category,
      q: q || undefined,
    })
      .then((list) => {
        if (!alive) return;
        setShops(list);
      })
      .catch((e) => {
        if (!alive) return;
        setShops([]);
        const status = e?.response?.status;
        setError({
          title: "Couldn't load shops",
          detail:
            status
              ? `Backend returned ${status}. Please try again in a moment.`
              : "Check your connection and try again.",
        });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [center.lat, center.lng, radius, category, q, reloadKey]);

  const featured = useMemo(() => shops.find((s) => s.verified) || shops[0] || null, [shops]);
  const recommended = useMemo(() => shops.filter((s) => !featured || s.id !== featured.id), [shops, featured]);

  const onGeo = (value) => {
    setGeoQuery(value);
    clearTimeout(debounceRef.current);
    if (value.length < 3) {
      setGeoResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode/search", { params: { q: value } });
        setGeoResults(data?.results || []);
      } catch {
        setGeoResults([]);
      }
    }, 400);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude, name: "Your location" };
        setCenter(next);
        localStorage.setItem("nd_center", JSON.stringify(next));
      },
      () => {}
    );
  };

  const pickLocation = (g) => {
    const next = { lat: g.lat, lng: g.lng, name: g.display_name.split(",")[0] };
    setCenter(next);
    localStorage.setItem("nd_center", JSON.stringify(next));
    setGeoQuery("");
    setGeoResults([]);
  };

  return (
    <div className="px-5 pt-4">
      {/* Top row: location pill + avatar */}
      <div className="flex items-center justify-between mb-5">
        <button
          data-testid="home-location"
          onClick={useMyLocation}
          className="flex items-center gap-2 bg-secondary/70 border border-border rounded-full pl-3 pr-4 py-2 max-w-[70%]"
        >
          <MapPin className="w-4 h-4 text-primary shrink-0" />
          <span className="font-semibold text-sm truncate">{center.name || "Set location"}</span>
          <Locate className="w-4 h-4 text-primary shrink-0" />
        </button>
        <MenuButton />
      </div>

      {/* Title + subtitle */}
      <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("home.shopsNearYou")}</h1>
      <p className="text-muted-foreground text-sm mt-1">{t("home.subtitle")}</p>

      {/* Search */}
      <div className="relative mt-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="home-search"
          value={q || geoQuery}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            onGeo(v);
          }}
          placeholder={t("home.searchPlaceholder")}
          className="w-full rounded-full bg-card border border-border pl-11 pr-11 py-3.5 text-sm font-medium focus:outline-none focus:border-primary transition-colors"
        />
        <Mic className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        {geoResults.length > 0 && (
          <div className="absolute z-40 mt-2 left-0 right-0 bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
            {geoResults.slice(0, 5).map((g, i) => (
              <button
                key={i}
                data-testid={`home-geo-${i}`}
                onClick={() => pickLocation(g)}
                className="block w-full text-left px-4 py-3 text-sm hover:bg-secondary transition-colors border-b border-border last:border-0"
              >
                {g.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Category dropdown */}
      <button
        data-testid="home-category-toggle"
        onClick={() => setCatOpen((v) => !v)}
        className="mt-3 w-full flex items-center justify-between bg-card border border-border rounded-2xl px-3 py-3.5"
      >
        <span className="flex items-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#DBE7FF] text-[#345DDB]">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <circle cx="6" cy="6" r="1.4" /><circle cx="12" cy="6" r="1.4" /><circle cx="18" cy="6" r="1.4" />
              <circle cx="6" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="18" cy="12" r="1.4" />
              <circle cx="6" cy="18" r="1.4" /><circle cx="12" cy="18" r="1.4" /><circle cx="18" cy="18" r="1.4" />
            </svg>
          </span>
          <span className="font-bold text-lg capitalize">
            {category === "all" ? t("home.all") : category}
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 transition-transform ${catOpen ? "rotate-180" : ""}`} />
      </button>
      {catOpen && (
        <div className="mt-2 bg-card rounded-2xl border border-border p-2 max-h-64 overflow-y-auto" data-testid="home-category-list">
          <CategoryRow active={category === "all"} label={t("home.all")} onClick={() => { setCategory("all"); setCatOpen(false); }} />
          {categories.map((c) => (
            <CategoryRow
              key={c.id}
              active={category === c.id}
              label={c.name}
              Icon={iconForCategory(c.id)}
              onClick={() => { setCategory(c.id); setCatOpen(false); }}
            />
          ))}
        </div>
      )}

      {/* Radius chips */}
      <div className="mt-5 flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground pr-2 shrink-0">
          <svg viewBox="0 0 24 24" width="16" height="16" className="text-primary" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 12a8 8 0 1 1 16 0" /><path d="M8 12a4 4 0 1 1 8 0" /><circle cx="12" cy="12" r="1" fill="currentColor" />
          </svg>
          {t("home.within")}
        </span>
        {RADII.map((r) => (
          <button
            key={r}
            data-testid={`home-radius-${r}`}
            onClick={() => setRadius(r)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-bold border transition-colors ${
              radius === r
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent border-border text-foreground hover:border-primary"
            }`}
          >
            {r} {t("home.km")}
          </button>
        ))}
      </div>

      {/* From shops you love — promo card */}
      <div className="mt-6">
        <p className="flex items-center gap-2 font-display font-extrabold text-lg mb-3">
          <Megaphone className="w-5 h-5 text-primary" /> {t("home.fromShopsYouLove")}
        </p>
        <div className="rounded-2xl bg-[#FDEEE6] border border-primary/10 p-5">
          <span className="inline-block text-[11px] font-extrabold tracking-widest uppercase text-primary bg-primary/10 rounded-full px-3 py-1">
            Tasty food
          </span>
          <p className="mt-3 font-bold text-lg">Hi follower</p>
          <p className="text-sm text-foreground/80 mt-1">Get 10% off with coupon code <b>welcome10</b></p>
        </div>
      </div>

      {/* Featured for you */}
      <div className="mt-7">
        <p className="flex items-center gap-2 font-display font-extrabold text-lg mb-3">
          <Star className="w-5 h-5 fill-primary text-primary" /> {t("home.featuredForYou")}
        </p>
        {loading ? (
          <div className="h-40 rounded-3xl bg-secondary animate-pulse" />
        ) : featured ? (
          <button
            data-testid={`home-featured-${featured.id}`}
            onClick={() => nav(`/shops/${featured.id}?tab=offers`)}
            className="w-full text-left rounded-3xl border-2 border-primary/60 bg-card p-5 hover:border-primary transition-colors"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-extrabold bg-primary text-primary-foreground rounded-full px-3 py-1.5">
              <Star className="w-3 h-3 fill-current" /> STATE
            </span>
            <p className="mt-4 font-display font-extrabold text-2xl leading-tight">{featured.name}</p>
            <p className="text-primary font-bold text-sm uppercase tracking-wide mt-1">
              {featured.category} · {featured.address?.split(",")[0]}
            </p>
            <span className="inline-block mt-4 text-sm font-bold bg-[#F7B267] text-[#3D2A17] rounded-full px-3 py-1">
              {featured.rating >= 4.5 ? "Top rated" : "Popular"}
            </span>
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">No featured shop right now.</p>
        )}
      </div>

      {/* Recommended Nearby grid */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between mb-4">
          <p className="font-display font-extrabold text-2xl">{t("home.recommendedNearby")}</p>
          <span className="text-sm text-muted-foreground font-semibold">
            {t("home.shopsCount", recommended.length)}
          </span>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 rounded-2xl bg-secondary animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div
            data-testid="home-error"
            className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center"
          >
            <p className="font-display font-bold text-lg text-foreground">{error.title}</p>
            <p className="text-sm text-muted-foreground mt-1">{error.detail}</p>
            <button
              data-testid="home-retry"
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-4 rounded-full bg-primary text-primary-foreground font-semibold px-5 py-2 hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : recommended.length === 0 ? (
          <div className="grid place-items-center py-16 text-center">
            <MapPin className="w-8 h-8 text-muted-foreground mb-3" />
            <p className="font-display font-bold text-lg">{t("home.noShops")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("home.tryWider")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="home-shop-grid">
            {recommended.map((s) => (
              <MobileShopCard key={s.id} shop={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ active, label, Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
        active ? "bg-primary/10 text-primary" : "hover:bg-secondary"
      }`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : <span className="w-4 h-4" />}
      <span className="font-semibold text-sm capitalize">{label}</span>
    </button>
  );
}

function MobileShopCard({ shop }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [fav, setFav] = useState(() => isFavorite(user?.email, shop.id));
  useEffect(() => {
    const on = () => setFav(isFavorite(user?.email, shop.id));
    window.addEventListener("nd_favorites_changed", on);
    return () => window.removeEventListener("nd_favorites_changed", on);
  }, [user?.email, shop.id]);
  const onFav = (e) => {
    e.stopPropagation();
    setFav(toggleFavorite(user?.email, shop));
  };
  return (
    <div className="relative">
      <button
        data-testid={`mobile-shop-${shop.id}`}
        onClick={() => nav(`/shops/${shop.id}`)}
        className="text-left w-full bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/40 transition-colors"
      >
        <div className="relative aspect-square overflow-hidden">
          <img src={shop.images?.[0]} alt={shop.name} className="w-full h-full object-cover" />
          {shop.verified && (
            <span className="absolute top-2 left-2 grid place-items-center w-6 h-6 rounded-full bg-[#3B82F6] text-white shadow">
              <BadgeCheck className="w-4 h-4" />
            </span>
          )}
        </div>
        <div className="p-3">
          <p className="font-bold text-sm truncate">{shop.name.toUpperCase()}</p>
          <p className="text-primary text-xs font-semibold mt-0.5 capitalize truncate">
            {shop.category} · {shop.address?.split(",")[0] || "—"}
          </p>
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="flex items-center gap-1 font-semibold text-foreground/80">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-primary">
                <path d="M12 2L2 22l10-4 10 4z" />
              </svg>
              {typeof shop.distance_km === "number" ? `${shop.distance_km} km` : "—"}
            </span>
            <span className="flex items-center gap-0.5 text-primary font-bold">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-3 h-3 fill-current" />
              ))}
              <span className="ml-1 text-foreground/80">{shop.rating}</span>
            </span>
          </div>
        </div>
      </button>
      <button
        data-testid={`fav-btn-${shop.id}`}
        onClick={onFav}
        aria-label={fav ? "Unfavorite" : "Favorite"}
        className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-card/90 backdrop-blur border border-border shadow hover:scale-105 transition-transform"
      >
        <Heart className={`w-4 h-4 ${fav ? "fill-primary text-primary" : "text-muted-foreground"}`} />
      </button>
    </div>
  );
}
